import { Link, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { AgeGate, useAgeGate } from '@/components/legal/age-gate';
import { LegalFooter } from '@/components/legal/footer';

interface AppShellProps {
  children: ReactNode;
}

/**
 * Shell commun a toutes les pages : header avec logo + slot enfant + footer legal.
 *
 * Polish mobile :
 *   - min-h-svh : utilise svh au lieu de vh pour eviter le bug iOS Safari
 *     (la barre d'URL qui rentre/sort fait varier le viewport et casse les layouts).
 *   - safe-area-inset-* : padding top/bottom pour respecter les "encoches"
 *     iPhone (notch / Dynamic Island / barre home virtuelle).
 *   - bg gradient ember subtil : feu mourant qui couvre tout le shell.
 *
 * AgeGate : modale 18+ affichee au premier acces. On la skip sur les pages
 * /legal/* pour que l'utilisateur puisse consulter les CGU/Privacy/Mentions
 * AVANT d'accepter (sinon UX impossible : "accepte sans lire").
 */
export function AppShell({ children }: AppShellProps) {
  const { verified, accept } = useAgeGate();
  const location = useLocation();
  const isLegalRoute = location.pathname.startsWith('/legal/');
  const isAdminRoute = location.pathname.startsWith('/admin');
  // AgeGate skip pour les routes legal (lecture CGU avant accept) et /admin
  // (admin = adulte par definition, pas besoin de re-confirmer).
  const showAgeGate = !verified && !isLegalRoute && !isAdminRoute;

  return (
    <div
      className="min-h-svh flex flex-col bg-gradient-to-b from-ember-deep via-ember-deep to-ember-coal"
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
      }}
    >
      <header className="border-b border-border backdrop-blur-sm">
        <div className="container mx-auto py-4 flex items-center justify-between">
          <Link
            to="/"
            className="text-2xl font-bold text-primary tracking-tight rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            aria-label="Ember - retour a l'accueil"
          >
            Ember
          </Link>
          <span className="text-xs text-muted-foreground italic hidden sm:inline">
            Joue, explore, en toute liberté.
          </span>
        </div>
      </header>
      <main className="flex-1 container mx-auto py-8 sm:py-12">{children}</main>
      <LegalFooter />

      {showAgeGate && (
        <AgeGate
          onAccept={accept}
          onDecline={() => {
            /* la redirection est gérée dans AgeGate.handleDecline */
          }}
        />
      )}
    </div>
  );
}
